import { memo, useCallback } from 'react';
import { Row, flexRender } from '@tanstack/react-table';
import {
  ChevronRight,
  ChevronDown,
  GripVertical,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { useTaskPanelStore } from '@/stores/taskPanelStore';
import { useViewStore } from '@/stores/useViewStore';

interface Task {
  id: string;
  rowNumber: number;
  outlineLevel: number;
  isSummary: boolean;
  isExpanded: boolean;
  name: string;
  [key: string]: unknown;
}

interface GridRowProps<TData extends Task> {
  row: Row<TData>;
  isSelected: boolean;
  onSelect: (rowId: string, selected: boolean) => void;
  onExpand: (rowId: string) => void;
  onIndent: (rowId: string) => void;
  onOutdent: (rowId: string) => void;
  onDelete: (rowId: string) => void;
  onInsertAbove: (rowId: string) => void;
  onInsertBelow: (rowId: string) => void;
  onDragStart?: (rowId: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  virtualRowIndex?: number;
  style?: React.CSSProperties;
}

const INDENT_WIDTH = 20;
const CHECKBOX_WIDTH = 40;
const ROW_NUMBER_WIDTH = 50;

function GridRowComponent<TData extends Task>({
  row,
  isSelected,
  onSelect,
  onExpand,
  onIndent,
  onOutdent,
  onDelete,
  onInsertAbove,
  onInsertBelow,
  onDragStart,
  onDragEnd,
  isDragging = false,
  style,
}: GridRowProps<TData>) {
  const task = row.original;
  const indentLevel = task.outlineLevel - 1; // outlineLevel starts at 1
  const openPanel = useTaskPanelStore((state) => state.openPanel);
  const setHoveredTaskId = useViewStore((state) => state.setHoveredTaskId);
  const hoveredTaskId = useViewStore((state) => state.hoveredTaskId);
  const isHoveredFromGantt = hoveredTaskId === task.id;

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onSelect(task.id, checked);
    },
    [task.id, onSelect]
  );

  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onExpand(task.id);
    },
    [task.id, onExpand]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
      onDragStart?.(task.id);
    },
    [task.id, onDragStart]
  );

  const handleDragEnd = useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  const isCritical = task.isCritical === true;
  const isComplete = task.percentComplete === 100 || task.status === 'completed';
  const finishDate = typeof task.finishDate === 'string' ? task.finishDate : null;
  const isOverdue = Boolean(finishDate && new Date(finishDate) < new Date() && !isComplete);

  const rowClasses = cn(
    'flex items-stretch border-b border-gray-200 hover:bg-gray-50 group',
    isSelected && 'bg-blue-50 hover:bg-blue-100',
    isDragging && 'opacity-50',
    task.isSummary && 'bg-gray-50 font-semibold',
    isCritical && !isSelected && !isComplete && 'border-l-4 border-l-red-500 bg-red-50/30',
    isOverdue && !isSelected && 'border-l-4 border-l-amber-500 bg-amber-50/30',
    isComplete && !isSelected && 'opacity-60',
    isHoveredFromGantt && !isSelected && 'bg-indigo-50',
  );

  return (
    <div
      className={rowClasses}
      style={style}
      role="row"
      aria-selected={isSelected}
      onMouseEnter={() => setHoveredTaskId(task.id)}
      onMouseLeave={() => setHoveredTaskId(null)}
    >
      {/* Drag handle — frozen */}
      <div
        className="w-6 flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100 transition-opacity sticky left-0 z-10 bg-inherit"
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>

      {/* Row number — frozen */}
      <div
        className="flex items-center justify-center text-xs text-blue-600 border-r border-gray-200 cursor-pointer hover:bg-blue-50 hover:text-blue-800 sticky left-6 z-10 bg-inherit"
        style={{ width: ROW_NUMBER_WIDTH }}
        onClick={() => openPanel(task.id)}
        title="Click to open task details"
      >
        {task.rowNumber}
      </div>

      {/* Checkbox — frozen */}
      <div
        className="flex items-center justify-center border-r border-gray-200 sticky left-[74px] z-10 bg-inherit"
        style={{ width: CHECKBOX_WIDTH }}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          aria-label={`Select row ${task.rowNumber}`}
        />
      </div>

      {/* Task name with hierarchy */}
      <div
        className="flex items-center border-r border-gray-200 overflow-hidden"
        style={{
          width: row.getVisibleCells()[0]?.column.getSize(),
          minWidth: row.getVisibleCells()[0]?.column.getSize(),
          maxWidth: row.getVisibleCells()[0]?.column.getSize(),
          paddingLeft: indentLevel * INDENT_WIDTH + 8,
        }}
      >
        {/* Expand/collapse button for summary tasks */}
        {task.isSummary ? (
          <button
            className="p-0.5 mr-1 hover:bg-gray-200 rounded"
            onClick={handleExpandClick}
            aria-label={task.isExpanded ? 'Collapse' : 'Expand'}
          >
            {task.isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            )}
          </button>
        ) : (
          <span className="w-5" /> // Spacer for alignment
        )}

        {/* Task name - this will be rendered by the cell */}
        <div className="flex-1 min-w-0 truncate">
          {flexRender(row.getVisibleCells()[0]?.column.columnDef.cell, row.getVisibleCells()[0]?.getContext())}
        </div>
      </div>

      {/* Other columns */}
      {row.getVisibleCells().slice(1).map((cell) => (
        <div
          key={cell.id}
          className="flex items-center border-r border-gray-200 overflow-hidden"
          style={{
            width: cell.column.getSize(),
            minWidth: cell.column.getSize(),
            maxWidth: cell.column.getSize(),
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}

      {/* Row context menu */}
      <div className="w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 hover:bg-gray-200 rounded">
              <MoreHorizontal className="h-4 w-4 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onInsertAbove(task.id)}>
              Insert row above
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onInsertBelow(task.id)}>
              Insert row below
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onIndent(task.id)}>
              Indent
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOutdent(task.id)}>
              Outdent
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => onDelete(task.id)}
            >
              Delete row
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export const GridRow = memo(GridRowComponent) as typeof GridRowComponent;

export default GridRow;
